import { useMemo, useState } from 'react'
import { Tags, Pencil, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { Dialog } from '@/components/ui/dialog'
import { Input, Field } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import {
  fetchAppCategories,
  createAppCategory,
  updateAppCategory,
  deleteAppCategory,
  APP_MOD_STR as S,
  type AppCategoryRow,
} from './appModApi'

type EditTarget = AppCategoryRow | 'new' | null

/**
 * A5.22 — Kategoriya va teg'lar (Mini App katalogi). GET /admin/mini-apps/categories
 * (lane-A greenfield → fail-soft bo'sh). CRUD → POST/PUT/DELETE .../categories.
 */
export function AppCategoriesScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<EditTarget>(null)
  const [deleteTarget, setDeleteTarget] = useState<AppCategoryRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<AppCategoryRow>[]>(
    () => [
      {
        key: 'title',
        header: S.categoryTitle,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Tags size={14} className="text-tertiary" />
            <span className="font-medium text-primary">{r.title}</span>
            <code className="text-xs text-tertiary">{r.slug}</code>
          </span>
        ),
      },
      {
        key: 'tags',
        header: S.tags,
        type: 'text',
        minWidth: 180,
        render: (r) =>
          r.tags && r.tags.length ? (
            <div className="flex flex-wrap gap-1">
              {r.tags.map((tg) => (
                <span key={tg} className="rounded-sm bg-surface-3 px-1.5 py-0.5 text-xs text-secondary">
                  {tg}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      { key: 'app_count', header: S.appCount, type: 'number', width: 110, align: 'right' },
      { key: 'position', header: S.position, type: 'number', width: 100, align: 'right' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AppCategoryRow>[]>(
    () => [
      { id: 'edit', label: S.editCategory, icon: <Pencil size={13} />, onSelect: (r) => setTarget(r) },
      {
        id: 'delete',
        label: S.deleteCategory,
        icon: <Trash2 size={13} />,
        danger: true,
        onSelect: (r) => setDeleteTarget(r),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title={S.catTitle}
        screenId="A5.22"
        actions={
          <Button size="sm" onClick={() => setTarget('new')}>
            <Plus size={14} /> {S.addCategory}
          </Button>
        }
      />
      <DataTable<AppCategoryRow>
        key={reloadKey}
        screenId="A5.22"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchAppCategories}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Tags size={28} strokeWidth={1.5} />, title: S.catEmpty }}
      />

      {target && (
        <CategoryDialog
          target={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
            toast(S.done, { tone: 'success' })
          }}
          onError={(e) =>
            toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
          }
        />
      )}

      {deleteTarget && (
        <DangerDialog
          open
          onClose={() => setDeleteTarget(null)}
          screenId="A5.22"
          level={2}
          title={S.deleteCatTitle}
          description={S.deleteCatDesc}
          confirmLabel={S.deleteCategory}
          onConfirm={async () => {
            try {
              await deleteAppCategory(deleteTarget.id)
              toast(S.done, { tone: 'success' })
              setDeleteTarget(null)
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
              throw e
            }
          }}
        />
      )}
    </>
  )
}

function CategoryDialog({
  target,
  onClose,
  onSaved,
  onError,
}: {
  target: EditTarget
  onClose: () => void
  onSaved: () => void
  onError: (e: unknown) => void
}) {
  const editing = target !== 'new' && target !== null ? target : null
  const [title, setTitle] = useState(editing?.title ?? '')
  const [slug, setSlug] = useState(editing?.slug ?? '')
  const [tags, setTags] = useState((editing?.tags ?? []).join(', '))
  const [position, setPosition] = useState(String(editing?.position ?? 0))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!title.trim()) return
    setBusy(true)
    try {
      const body = {
        title: title.trim(),
        slug: slug.trim() || title.trim().toLowerCase().replace(/\s+/g, '-'),
        tags: tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        position: parseInt(position, 10) || 0,
      }
      if (editing) await updateAppCategory(editing.id, body)
      else await createAppCategory(body)
      onSaved()
    } catch (e) {
      setBusy(false)
      onError(e)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? S.editCategory : S.newCategory}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {S.cancel}
          </Button>
          <Button onClick={save} disabled={busy || !title.trim()}>
            {S.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={S.categoryTitle}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </Field>
        <Field label={S.slug}>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} mono placeholder="games" />
        </Field>
        <Field label={S.tags}>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="arcade, casual" />
        </Field>
        <Field label={S.position}>
          <Input value={position} onChange={(e) => setPosition(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
    </Dialog>
  )
}
